import type { ClarifyingQuestion } from '../types';

type QuestionPool = {
  keywords: string[];
  questions: ClarifyingQuestion[];
};

const genericQuestions: ClarifyingQuestion[] = [
  {
    id: 'q1',
    question: 'What is the primary problem this feature should solve?',
    defaultAnswer: 'Product teams lose informal requirement context scattered across Slack threads.',
  },
  {
    id: 'q2',
    question: 'Who are the main users?',
    defaultAnswer: 'Product managers, engineers, and designers discussing features in Slack.',
  },
  {
    id: 'q3',
    question: 'What is the single most important success metric?',
    defaultAnswer: 'A user can mention the bot, answer clarifying questions, and receive a PRD file.',
  },
  {
    id: 'q4',
    question: 'Are there any must-have integrations in v1?',
    defaultAnswer: 'No real integrations — all Slack data is mocked inline.',
  },
  {
    id: 'q5',
    question: 'What should happen after the PRD is approved?',
    defaultAnswer: 'The bot posts a concise summary and a downloadable prd.md file to the thread.',
  },
];

const keywordPools: QuestionPool[] = [
  {
    keywords: ['auth', 'login', 'oauth', 'password', 'pin', 'otp'],
    questions: [
      {
        id: 'auth-1',
        question: 'Which authentication methods must be supported in v1?',
        defaultAnswer: 'PIN, password, and OTP; OAuth and social login are nice-to-haves.',
      },
      {
        id: 'auth-2',
        question: 'What happens when authentication fails repeatedly?',
        defaultAnswer: 'Lock the account after 5 failed attempts and notify the user.',
      },
      {
        id: 'auth-3',
        question: 'Should sessions expire or be long-lived?',
        defaultAnswer: 'Sessions expire after 24 hours of inactivity.',
      },
    ],
  },
  {
    keywords: ['payment', 'pay', 'checkout', 'transfer', 'cash-in', 'cash-out'],
    questions: [
      {
        id: 'pay-1',
        question: 'What payment channels are supported at launch?',
        defaultAnswer: 'Cards, bank transfer, and wallet-to-wallet transfers.',
      },
      {
        id: 'pay-2',
        question: 'Is real-time balance validation required?',
        defaultAnswer: 'Yes — check available balance before confirming any transfer.',
      },
      {
        id: 'pay-3',
        question: 'Should failed payments be retried automatically?',
        defaultAnswer: 'Retry once immediately, then surface a manual retry option to the user.',
      },
    ],
  },
  {
    keywords: ['notification', 'notify', 'email', 'push', 'sms'],
    questions: [
      {
        id: 'notif-1',
        question: 'Which notification channels are required in v1?',
        defaultAnswer: 'Email and in-app push notifications; SMS is out of scope.',
      },
      {
        id: 'notif-2',
        question: 'Can users manage their notification preferences?',
        defaultAnswer: 'Yes — users can toggle channels per notification category.',
      },
      {
        id: 'notif-3',
        question: 'Should notifications batch or be immediate?',
        defaultAnswer: 'Immediate for critical alerts; batched daily for non-critical updates.',
      },
    ],
  },
  {
    keywords: ['offline', 'sync', 'connection'],
    questions: [
      {
        id: 'offline-1',
        question: 'What features must work without an internet connection?',
        defaultAnswer: 'Viewing cached data and drafting new entries.',
      },
      {
        id: 'offline-2',
        question: 'How should the app behave when connectivity returns?',
        defaultAnswer: 'Silently sync local drafts and refresh the current view.',
      },
    ],
  },
  {
    keywords: ['audit', 'log', 'trace'],
    questions: [
      {
        id: 'audit-1',
        question: 'Which actions must be captured in the audit trail?',
        defaultAnswer: 'All state-changing actions performed by authenticated users.',
      },
      {
        id: 'audit-2',
        question: 'How long should audit records be retained?',
        defaultAnswer: 'Retain for 7 years, with searchable access for the last 2 years.',
      },
    ],
  },
];

export function selectQuestions(threadText: string): ClarifyingQuestion[] {
  const normalized = threadText.toLowerCase();
  const selected = new Map<string, ClarifyingQuestion>();

  for (const pool of keywordPools) {
    if (pool.keywords.some((kw) => normalized.includes(kw))) {
      for (const q of pool.questions) {
        selected.set(q.id, q);
      }
    }
  }

  const result = Array.from(selected.values());
  if (result.length === 0) {
    return genericQuestions.slice(0, 5);
  }

  // Fill any remaining slots with generics that are not already covered by id prefix.
  for (const g of genericQuestions) {
    if (result.length >= 5) break;
    if (!result.some((q) => q.id.split('-')[0] === g.id)) {
      result.push(g);
    }
  }

  return result.slice(0, 5);
}
